import { useParams, Link } from "wouter";
import { InfoTip } from "@/components/ui/info-tip";
import { FORMULAS } from "@/lib/formulas";
import { 
  useGetCustomer, 
  useUpdateCustomer, 
  useDeleteCustomer, 
  useListEstimates,
  useListInvoices,
  useDeleteCustomerSavedCard,
  getGetCustomerQueryKey,
  getListCustomersQueryKey
} from "@workspace/api-client-react";
import { ProGate } from "@/components/pro-gate";
import { CustomerAttachmentsPanel } from "@/components/customer-attachments-panel";
import { usePageTitle } from "@/hooks/use-page-title";
import { useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { ArrowLeft, Edit, Trash2, Mail, Phone, MapPin, Plus, Loader2, Calendar, FileText, CreditCard, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

const customerSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  addressLine1: z.string().optional().or(z.literal("")),
  addressLine2: z.string().optional().or(z.literal("")),
  city: z.string().optional().or(z.literal("")),
  state: z.string().optional().or(z.literal("")),
  postalCode: z.string().optional().or(z.literal("")),
  country: z.string().optional().or(z.literal("")),
  shippingAddressLine1: z.string().optional().or(z.literal("")),
  shippingAddressLine2: z.string().optional().or(z.literal("")),
  shippingCity: z.string().optional().or(z.literal("")),
  shippingState: z.string().optional().or(z.literal("")),
  shippingPostalCode: z.string().optional().or(z.literal("")),
  shippingCountry: z.string().optional().or(z.literal("")),
  paymentTermsDays: z.union([z.coerce.number().int().min(0), z.literal("")]).optional(),
  notes: z.string().optional().or(z.literal("")),
  taxExempt: z.boolean().optional(),
});

function formatAddress(c: {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}): string {
  return [
    c.addressLine1,
    c.addressLine2,
    [c.city, c.state].filter(Boolean).join(", "),
    c.postalCode,
    c.country,
  ].filter(Boolean).join(", ");
}

export default function CustomerDetail() {
  const params = useParams();
  const customerId = parseInt(params.id || "0", 10);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [diffShipping, setDiffShipping] = useState(false);

  const { data: customer, isLoading: isLoadingCustomer } = useGetCustomer(customerId, {
    query: { enabled: !!customerId, queryKey: getGetCustomerQueryKey(customerId) }
  });

  useEffect(() => {
    if (customer) {
      setDiffShipping(!!(customer.shippingAddressLine1 || customer.shippingCity));
    }
  }, [customer?.id]);

  usePageTitle(customer?.name ?? "Customer");

  const { data: estimates, isLoading: isLoadingEstimates } = useListEstimates({ customerId });
  const { data: invoices, isLoading: isLoadingInvoices } = useListInvoices({ customerId });

  const updateCustomer = useUpdateCustomer();
  const deleteCustomer = useDeleteCustomer();
  const deleteCustomerSavedCard = useDeleteCustomerSavedCard();

  const form = useForm<z.infer<typeof customerSchema>>({
    resolver: zodResolver(customerSchema),
    values: {
      name: customer?.name || "",
      email: customer?.email || "",
      phone: customer?.phone || "",
      addressLine1: customer?.addressLine1 || "",
      addressLine2: customer?.addressLine2 || "",
      city: customer?.city || "",
      state: customer?.state || "",
      postalCode: customer?.postalCode || "",
      country: customer?.country || "",
      shippingAddressLine1: customer?.shippingAddressLine1 || "",
      shippingAddressLine2: customer?.shippingAddressLine2 || "",
      shippingCity: customer?.shippingCity || "",
      shippingState: customer?.shippingState || "",
      shippingPostalCode: customer?.shippingPostalCode || "",
      shippingCountry: customer?.shippingCountry || "",
      paymentTermsDays: customer?.paymentTermsDays ?? "",
      notes: customer?.notes || "",
      taxExempt: customer?.taxExempt ?? false,
    },
  });

  const onUpdate = (data: z.infer<typeof customerSchema>) => {
    const payload = {
      ...data,
      addressLine1: data.addressLine1 || undefined,
      addressLine2: data.addressLine2 || undefined,
      city: data.city || undefined,
      state: data.state || undefined,
      postalCode: data.postalCode || undefined,
      country: data.country || undefined,
      shippingAddressLine1: diffShipping ? (data.shippingAddressLine1 || undefined) : null,
      shippingAddressLine2: diffShipping ? (data.shippingAddressLine2 || undefined) : null,
      shippingCity: diffShipping ? (data.shippingCity || undefined) : null,
      shippingState: diffShipping ? (data.shippingState || undefined) : null,
      shippingPostalCode: diffShipping ? (data.shippingPostalCode || undefined) : null,
      shippingCountry: diffShipping ? (data.shippingCountry || undefined) : null,
      paymentTermsDays: typeof data.paymentTermsDays === "number" ? data.paymentTermsDays : undefined,
      notes: data.notes || undefined,
    };
    updateCustomer.mutate({ id: customerId, data: payload }, {
      onSuccess: (updatedCustomer) => {
        queryClient.setQueryData(getGetCustomerQueryKey(customerId), updatedCustomer);
        setIsEditOpen(false);
        toast({ title: "Customer updated" });
      },
      onError: () => {
        toast({ title: "Failed to update customer", variant: "destructive" });
      }
    });
  };

  const onDelete = () => {
    deleteCustomer.mutate({ id: customerId }, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
        toast({ title: "Customer deleted" });
        setLocation("/customers");
      },
      onError: () => {
        toast({ title: "Failed to delete customer", variant: "destructive" });
      }
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "accepted": return "bg-green-100 text-green-800 border-green-200";
      case "sent": return "bg-blue-100 text-blue-800 border-blue-200";
      case "declined": return "bg-red-100 text-red-800 border-red-200";
      case "converted": return "bg-purple-100 text-purple-800 border-purple-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getEstimateStatusLabel = (status: string) => {
    if (status === "converted") return "Converted to Invoice";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case "paid": return "bg-green-100 text-green-800 border-green-200";
      case "sent": return "bg-blue-100 text-blue-800 border-blue-200";
      case "overdue": return "bg-red-100 text-red-800 border-red-200";
      case "void": return "bg-gray-100 text-gray-500 border-gray-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (isLoadingCustomer) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-8 w-64 mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-48" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!customer) {
    return <div className="text-center py-12">Customer not found</div>;
  }

  // Mirror the estimate dashboard's exclusions so per-customer totals reconcile
  // with the global dashboard. Change orders are already filtered server-side
  // in GET /estimates; recurring templates never become accepted on their own
  // and must be excluded here too.
  const fundamentalEstimates = estimates?.filter(e => !e.isRecurring) ?? [];
  const totalValue = fundamentalEstimates.reduce((sum, est) => sum + est.total, 0);
  const acceptedEstimates = fundamentalEstimates.filter(e => e.status === "accepted" || e.status === "converted");
  const acceptedValue = acceptedEstimates.reduce((sum, est) => sum + est.total, 0);

  // Sum actual payments across all invoices instead of filtering by status.
  // Partial-paid invoices were previously invisible — a customer who has paid
  // $50k on $100k of partial invoices now shows $50k, not $0.
  // Milestone children carry the actual payments (the milestone parent holds the
  // contract total but no payment rows), so include them here — excluding them
  // hid milestone collections entirely. Deposit children are still excluded: their
  // payments are mirrored onto the parent. Parents contribute paidAmount 0, so no
  // double counting. This mirrors the project paid-total rollup.
  const billableInvoices = invoices?.filter(inv =>
    !inv.isDepositChild && inv.status !== "void"
  ) ?? [];
  const paidInvoiceValue = billableInvoices.reduce((sum, inv) => sum + (inv.paidAmount ?? 0), 0);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <Link href="/customers">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" />
          Back to Customers
        </Button>
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{customer.name}</h1>
          <p className="text-muted-foreground flex items-center gap-1 mt-1 text-sm">
            <Calendar className="w-3.5 h-3.5" />
            Customer since {format(new Date(customer.createdAt), "MMMM yyyy")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link href={`/quotes/new?customer=${customer.id}`}>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Quote
            </Button>
          </Link>
          <Button variant="outline" onClick={() => setIsEditOpen(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      <hr className="border-border" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Contact Info */}
        <Card className="shadow-sm border-border/50 h-fit">
          <CardHeader>
            <CardTitle className="text-lg">Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Email</p>
                {customer.email ? (
                  <a href={`mailto:${customer.email}`} className="text-primary hover:underline">{customer.email}</a>
                ) : (
                  <p className="text-muted-foreground">Not provided</p>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3 text-sm">
              <Phone className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Phone</p>
                {customer.phone ? (
                  <a href={`tel:${customer.phone}`} className="text-primary hover:underline">{customer.phone}</a>
                ) : (
                  <p className="text-muted-foreground">Not provided</p>
                )}
              </div>
            </div>
            
            <div className="flex items-start gap-3 text-sm">
              <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Billing Address</p>
                {formatAddress(customer) ? (
                  <div className="text-muted-foreground space-y-0.5">
                    {customer.addressLine1 && <p>{customer.addressLine1}</p>}
                    {customer.addressLine2 && <p>{customer.addressLine2}</p>}
                    {(customer.city || customer.state) && <p>{[customer.city, customer.state].filter(Boolean).join(", ")}</p>}
                    {(customer.postalCode || customer.country) && <p>{[customer.postalCode, customer.country].filter(Boolean).join(" ")}</p>}
                  </div>
                ) : (
                  <p className="text-muted-foreground">Not provided</p>
                )}
              </div>
            </div>

            {formatAddress({ addressLine1: customer.shippingAddressLine1, addressLine2: customer.shippingAddressLine2, city: customer.shippingCity, state: customer.shippingState, postalCode: customer.shippingPostalCode, country: customer.shippingCountry }) && (
              <div className="flex items-start gap-3 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Shipping Address</p>
                  <div className="text-muted-foreground space-y-0.5">
                    {customer.shippingAddressLine1 && <p>{customer.shippingAddressLine1}</p>}
                    {customer.shippingAddressLine2 && <p>{customer.shippingAddressLine2}</p>}
                    {(customer.shippingCity || customer.shippingState) && <p>{[customer.shippingCity, customer.shippingState].filter(Boolean).join(", ")}</p>}
                    {(customer.shippingPostalCode || customer.shippingCountry) && <p>{[customer.shippingPostalCode, customer.shippingCountry].filter(Boolean).join(" ")}</p>}
                  </div>
                </div>
              </div>
            )}

            {customer.paymentTermsDays != null && (
              <div className="flex items-start gap-3 text-sm">
                <CreditCard className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Payment Terms</p>
                  <p className="text-muted-foreground">Net {customer.paymentTermsDays}</p>
                </div>
              </div>
            )}

            {customer.taxExempt && (
              <div className="flex items-start gap-3 text-sm">
                <ShieldCheck className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Tax Status</p>
                  <p className="text-muted-foreground">Tax exempt</p>
                </div>
              </div>
            )}

            {customer.stripeDefaultPaymentMethodId && (
              <div className="flex items-start gap-3 text-sm">
                <CreditCard className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">Payment</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                      Saved card on file
                    </Badge>
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-0.5 disabled:opacity-50"
                      disabled={deleteCustomerSavedCard.isPending}
                      onClick={() => {
                        deleteCustomerSavedCard.mutate({ id: customerId }, {
                          onSuccess: () => {
                            queryClient.invalidateQueries({ queryKey: getGetCustomerQueryKey(customerId) });
                            toast({ title: "Saved card removed" });
                          },
                          onError: () => {
                            toast({ title: "Failed to remove saved card", variant: "destructive" });
                          },
                        });
                      }}
                    >
                      {deleteCustomerSavedCard.isPending ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <X className="w-3 h-3" />
                      )}
                      Remove saved card
                    </button>
                  </div>
                </div>
              </div>
            )}

            {customer.notes && (
              <div className="flex items-start gap-3 text-sm">
                <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium text-foreground">Notes</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{customer.notes}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Estimate History */}
        <div className="md:col-span-2 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card style={{ borderTop: "3px solid #6366f1", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground mb-1.5 flex items-center gap-1">Total Quotes <InfoTip content={FORMULAS.customerTotalQuotes} /></p>
                <p className="text-2xl font-bold" style={{ color: "#6366f1" }}>{estimates?.length || 0}</p>
              </CardContent>
            </Card>
            <Card style={{ borderTop: "3px solid #3b82f6", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground mb-1.5 flex items-center gap-1">Total Value <InfoTip content={FORMULAS.customerTotalValue} /></p>
                <p className="text-2xl font-bold" style={{ color: "#3b82f6" }}>{formatCurrency(totalValue)}</p>
              </CardContent>
            </Card>
            <Card style={{ borderTop: "3px solid #22c55e", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.07em] text-muted-foreground mb-1.5 flex items-center gap-1">Accepted Value <InfoTip content={FORMULAS.customerAcceptedValue} /></p>
                <p className="text-2xl font-bold" style={{ color: "#22c55e" }}>{formatCurrency(acceptedValue)}</p>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-sm border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2 border-b bg-muted/40">
              <CardTitle className="text-lg">Quote History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingEstimates ? (
                <div className="p-6 space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : estimates?.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  No quotes created for this customer yet.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {estimates?.map(estimate => (
                    <Link key={estimate.id} href={`/quotes/${estimate.id}`}>
                      <div className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors group">
                        <div className="min-w-0 pr-4">
                          <p className="font-medium truncate group-hover:text-primary transition-colors">{estimate.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {format(new Date(estimate.createdAt), "MMM d, yyyy")}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <p className="font-semibold">{formatCurrency(estimate.total)}</p>
                          <Badge variant="outline" className={`justify-center ${estimate.status === "converted" ? "px-2" : "w-20"} ${getStatusColor(estimate.status)}`}>
                            {getEstimateStatusLabel(estimate.status)}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="shadow-sm border-border/50">
            <CardHeader className="flex flex-row items-center justify-between pb-2 border-b bg-muted/40">
              <CardTitle className="text-lg">Invoice History</CardTitle>
              <div className="flex items-center gap-2">
                {(invoices?.length ?? 0) > 0 && (
                  <span className="text-sm text-muted-foreground">{invoices?.length} invoice{(invoices?.length ?? 0) !== 1 ? "s" : ""}</span>
                )}
                <Link href={`/invoices/new?customer=${customer.id}`}>
                  <Button size="sm" variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    New Invoice
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingInvoices ? (
                <div className="p-6 space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (invoices?.length ?? 0) === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  No invoices created for this customer yet.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {invoices?.map(invoice => (
                    <Link key={invoice.id} href={`/invoices/${invoice.id}`}>
                      <div className="flex items-center justify-between p-4 hover:bg-muted/50 cursor-pointer transition-colors group">
                        <div className="min-w-0 pr-4">
                          <div className="flex items-center gap-2">
                            {invoice.invoiceNumber && (
                              <span className="text-xs font-mono text-muted-foreground">{invoice.invoiceNumber}</span>
                            )}
                            <p className="font-medium truncate group-hover:text-primary transition-colors">{invoice.title}</p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {invoice.dueDate ? `Due ${format(new Date(invoice.dueDate), "MMM d, yyyy")}` : format(new Date(invoice.createdAt), "MMM d, yyyy")}
                          </p>
                        </div>
                        <div className="flex items-center gap-4 shrink-0">
                          <p className="font-semibold">{formatCurrency(invoice.total)}</p>
                          <Badge variant="outline" className={`w-20 justify-center capitalize ${getInvoiceStatusColor(invoice.status)}`}>
                            {invoice.status}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Attachments Section */}
      <ProGate feature="FILE_ATTACHMENTS">
        <CustomerAttachmentsPanel customerId={customerId} />
      </ProGate>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onUpdate)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Customer Name / Company</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl><Input type="email" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl><Input type="tel" {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Billing Address</p>
                <FormField control={form.control} name="addressLine1" render={({ field }) => (
                  <FormItem><FormControl><Input placeholder="Address line 1" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <FormField control={form.control} name="addressLine2" render={({ field }) => (
                  <FormItem><FormControl><Input placeholder="Address line 2 (optional)" {...field} /></FormControl><FormMessage /></FormItem>
                )} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="city" render={({ field }) => (
                    <FormItem><FormControl><Input placeholder="City" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="state" render={({ field }) => (
                    <FormItem><FormControl><Input placeholder="State" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="postalCode" render={({ field }) => (
                    <FormItem><FormControl><Input placeholder="ZIP / Postal code" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="country" render={({ field }) => (
                    <FormItem><FormControl><Input placeholder="Country" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                </div>
              </div>

              <div className="flex items-center gap-2 py-1">
                <Switch
                  checked={diffShipping}
                  onCheckedChange={(checked) => {
                    setDiffShipping(checked);
                    if (!checked) {
                      form.setValue("shippingAddressLine1", "");
                      form.setValue("shippingAddressLine2", "");
                      form.setValue("shippingCity", "");
                      form.setValue("shippingState", "");
                      form.setValue("shippingPostalCode", "");
                      form.setValue("shippingCountry", "");
                    }
                  }}
                  id="diff-shipping-edit"
                />
                <label htmlFor="diff-shipping-edit" className="text-sm cursor-pointer">Different shipping address</label>
              </div>

              {diffShipping && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Shipping Address</p>
                  <FormField control={form.control} name="shippingAddressLine1" render={({ field }) => (
                    <FormItem><FormControl><Input placeholder="Address line 1" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="shippingAddressLine2" render={({ field }) => (
                    <FormItem><FormControl><Input placeholder="Address line 2 (optional)" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="shippingCity" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="City" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="shippingState" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="State" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="shippingPostalCode" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="ZIP / Postal code" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="shippingCountry" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="Country" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="paymentTermsDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Terms (days)</FormLabel>
                      <FormControl>
                        <Input type="number" min="0" placeholder="e.g. 30" {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="taxExempt"
                  render={({ field }) => (
                    <FormItem className="flex flex-col justify-end pb-1">
                      <FormLabel>Tax Exempt</FormLabel>
                      <div className="flex items-center gap-2 mt-1">
                        <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                        <span className="text-sm text-muted-foreground">{field.value ? "Yes" : "No"}</span>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Internal notes about this customer..." className="min-h-[60px] resize-none" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={updateCustomer.isPending}>
                  {updateCustomer.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Alert */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this customer. Any quotes associated with this customer will also be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => { e.preventDefault(); onDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteCustomer.isPending}
            >
              {deleteCustomer.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Customer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}